/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { LockOutlined } from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
	Stepper,
	Step,
	StepLabel,
	Button,
	Typography,
	TextField
} from '@material-ui/core';
import { useForm } from 'react-hook-form';
import {
	Elements,
	CardCvcElement,
	CardNumberElement,
	CardExpiryElement,
	useStripe,
	useElements
} from '@stripe/react-stripe-js';

import ReactHookFormInput from '../../common/input/ReactHookFormInput';
import StripeInput from '../../common/StripeInput';
import TranslationHelper from '../../../common/TranslationHelper';

import '../../../css/components/signup/signup.css';

const convertCustomAttributeForReactHookForm = (input) => {
	const data = {
		type: input.attributeType,
		validation: { required: input.isRequired },
		label: input.uniqueName.replace(/_/g, ' '),
		name: `customAttributes.${input.uniqueName}`,
		picklist: input.pickListElements,
		variant: 'outlined',
		style: { margin: '10px 0px' },
		fullWidth: true
	};

	switch (input.attributeType) {
	case 'Numeric':
	case 'Currency':
		data.type = 'number';
		break;
	case 'URL':
		data.type = 'text';
		data.validation.pattern = /^(ftp|http|https):\/\/[^ "]+$/;
		break;
	case 'Long_Text':
		data.multiline = true;
		data.rows = 4;
		break;
	default:
		break;
	}
	return data;
};

export default function SignUpForm(props) {
	const [activeStep, setActiveStep] = useState(0);
	const { watch, handleSubmit, ...useFormHooks } = useForm();
	const pass = watch('signUpPass', '');
	const [signupData, setSignupData] = useState({});
	const [disabled, setDisabled] = useState(false);
	const [errorPropsNumber, setErrorPropsNumber] = useState(null);
	const [errorPropsExpiry, setErrorPropsExpiry] = useState(null);
	const [errorPropsCVC, setErrorPropsCVC] = useState(null);
	const theme = useTheme();
	const {
		customerAttributes, paymentEnabled, onSignUp, handleOpenPolicy
	} = props;

	const inputFieldsSignUp = [
		{
			name: 'signUpName',
			label: 'Full Name',
			type: 'text',
			validation: { required: true },
			defaultValue: '',
			fullWidth: true,
			style: { margin: '10px 0px' },
			variant: 'outlined',
		},
		{
			name: 'signUpEmail',
			label: 'Email Address',
			type: 'text',
			// eslint-disable-next-line no-useless-escape
			validation: { required: true, pattern: /^([a-zA-Z0-9_\-\.\+]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/i },
			defaultValue: '',
			fullWidth: true,
			style: { margin: '10px 0px' },
			variant: 'outlined',
		},
		{
			name: 'signUpPass',
			label: 'Password',
			type: 'password',
			validation: { required: true, minLength: 8 },
			defaultValue: '',
			fullWidth: true,
			style: { margin: '10px 0px' },
			variant: 'outlined',
		},
		{
			name: 'signUpConfirmPass',
			label: 'Confirm Password',
			type: 'password',
			validation: { required: true, validate: (value) => value === pass },
			defaultValue: '',
			fullWidth: true,
			style: { margin: '10px 0px' },
			variant: 'outlined',
		}
	];

	const useStyles = makeStyles(() => ({
		step_label_root: {
			fontSize: '1rem',
		}
	}));

	const classes = useStyles();

	const getSteps = () => {
		if (paymentEnabled && customerAttributes && customerAttributes[0]) {
			return ['User Info', 'Additional Info', 'Payment Info'];
		} if (paymentEnabled && (!customerAttributes || !customerAttributes[0])) {
			return ['User Info', 'Payment Info'];
		} if (!paymentEnabled && customerAttributes && customerAttributes[0]) {
			return ['User Info', 'Additional Info'];
		}
		return ['User Info'];
	};

	const onUserInfoSubmit = (data) => {
		if (!paymentEnabled && (!customerAttributes || !customerAttributes[0])) {
			onSignUp({ ...data, setDisabled });
		} else {
			setSignupData((prevState) => ({
				...prevState,
				data
			}));
			handleNext();
		}
	};

	const onAdditionalInfoSubmit = (data) => {
		if (!paymentEnabled) {
			onSignUp({ ...signupData, data, setDisabled });
		} else {
			setSignupData((prevState) => ({
				...prevState,
				data
			}));
			handleNext();
		}
	};

	const steps = getSteps();

	const terms = (
		<div style={{ margin: '5px 0px' }}>
			{TranslationHelper.getValue('customer_account.signUp.notice')}
&nbsp;
			<a
				href="#/"
				onClick={(e) => { e.preventDefault(); handleOpenPolicy(e, 'terms'); }}
				style={{ cursor: 'pointer' }}
				data-cy="terms"
			>
				{TranslationHelper.getValue('customer_account.termsAndConditions.displayName')}
&nbsp;
			</a>
			{TranslationHelper.getValue('customer_account.singleWord.and')}
&nbsp;
			<a
				href="#/"
				onClick={(e) => { e.preventDefault(); handleOpenPolicy(e, 'privacy'); }}
				style={{ cursor: 'pointer' }}
				data-cy="privacy"
			>
				{TranslationHelper.getValue('customer_account.privacyPolicies.displayName')}
			</a>
			.
		</div>
	);

	const getStepContent = (props) => {
		switch (steps[props.activeStep]) {
		case 'User Info':
			return (
				<>
					{props.props.social}
					<form onSubmit={handleSubmit(onUserInfoSubmit)} className="input-form">
						{props.inputFieldsSignUp.map((input) => (
							<ReactHookFormInput
								key={input.name}
								{...{
									...input,
									useFormHooks
								}}
							/>
						))}
						{terms}
						<div style={{ padding: '10px 0px', display: 'flex', justifyContent: 'space-between' }}>
							{ activeStep === 0
								? null
								: (
									<Button
										variant="contained"
										onClick={handleBack}
										style={{ width: '48%', color: 'white' }}
									>
										Back
									</Button>
								)}
							<Button
								id={activeStep === steps.length - 1 ? 'ga-signUpWithEmail' : ''}
								className={activeStep === steps.length - 1 ? 'ga-signUp' : ''}
								variant="contained"
								color="primary"
								type="submit"
								style={{ color: 'white', width: activeStep === 0 ? '100%' : '48%' }}
								disabled={activeStep === steps.length - 1 && disabled}
								data-cy="signUpButton"
							>
								{activeStep === steps.length - 1
									? TranslationHelper.getValue('customer_account.modal.button.signUp')
									: 'Next'}
							</Button>
						</div>
					</form>
				</>
			);
		case 'Payment Info':
			return (
				<>
					<Elements stripe={props.props.stripe}>
						<StripeCardForm />
					</Elements>
				</>
			);
		case 'Additional Info':
			return (
				<form onSubmit={handleSubmit(onAdditionalInfoSubmit)}>
					<span />
					{props.props.customerAttributes.map((input) => {
						const inputProps = convertCustomAttributeForReactHookForm(input);
						return <ReactHookFormInput {...{ ...inputProps, useFormHooks }} />;
					})}
					{terms}
					<div style={{ padding: '10px 0px', display: 'flex', justifyContent: 'space-between' }}>
						{ activeStep === 0
							? null
							: (
								<Button
									variant="contained"
									onClick={handleBack}
									style={{ width: '48%', color: 'white' }}
								>
									Back
								</Button>
							)}
						<Button
							id={activeStep === steps.length - 1 ? 'ga-signUpWithEmail' : ''}
							className={activeStep === steps.length - 1 ? 'ga-signUp' : ''}
							variant="contained"
							color="primary"
							style={activeStep === 0
								? { width: '100%', color: 'white' }
								: { width: '48%', color: 'white' }}
							type="submit"
							disabled={activeStep === steps.length - 1 && disabled}
							data-cy="signUpButton"
						>
							{activeStep === steps.length - 1
								? TranslationHelper.getValue('customer_account.modal.button.signUp')
								: 'Next'}
						</Button>
					</div>
				</form>
			);
		default:
			return 'Unknown step';
		}
	};

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const StripeCardForm = () => {
		const stripe = useStripe();
		const elements = useElements();

		const onStripeInfoSubmit = async (event) => {
			event.preventDefault();

			if (elements == null) {
				return;
			}

			const cardElement = elements.getElement(CardNumberElement);
			const { error, token } = await stripe.createToken(cardElement);
			if (error) {
				setErrorPropsNumber({});
				setErrorPropsExpiry({});
				setErrorPropsCVC({});

				switch (error.code) {
				case 'incomplete_number':
					setErrorPropsNumber({
						error: true,
						helperText: error.message
					});
					break;
				case 'invalid_number':
					setErrorPropsNumber({
						error: true,
						helperText: error.message
					});
					break;
				case 'invalid_expiry_year_past':
					setErrorPropsExpiry({
						error: true,
						helperText: error.message
					});
					break;
				case 'incomplete_expiry':
					setErrorPropsExpiry({
						error: true,
						helperText: error.message
					});
					break;
				case 'incomplete_cvc':
					setErrorPropsCVC({
						error: true,
						helperText: error.message
					});
					break;
				default:
					break;
				}
			} else {
				onSignUp({ ...signupData, token, setDisabled });
			}
		};

		return (
			<form onSubmit={onStripeInfoSubmit}>
				<TextField
					label="Credit Card Number"
					name="ccnumber"
					variant="outlined"
					required
					{...errorPropsNumber}
					fullWidth
					style={{ margin: '10px 0px' }}
					InputLabelProps={{ shrink: true }}
					InputProps={{
						inputComponent: StripeInput,
						inputProps: {
							component: CardNumberElement,
							'data-cy': 'cardNumber'
						},
					}}
				/>
				<TextField
					label="Expiration Date"
					name="ccexp"
					variant="outlined"
					required
					{...errorPropsExpiry}
					fullWidth
					style={{ margin: '10px 0px' }}
					InputLabelProps={{ shrink: true }}
					InputProps={{
						inputComponent: StripeInput,
						inputProps: {
							component: CardExpiryElement
						},
					}}
				/>
				<TextField
					label="CVC"
					name="cvc"
					variant="outlined"
					fullWidth
					required
					{...errorPropsCVC}
					style={{ margin: '10px 0px' }}
					InputLabelProps={{ shrink: true }}
					InputProps={{
						inputComponent: StripeInput,
						inputProps: {
							component: CardCvcElement
						},
					}}
				/>
				{terms}
				<div style={{ padding: '10px 0px', display: 'flex', justifyContent: 'space-between' }}>
					{ activeStep === 0
						? null
						: (
							<Button
								variant="contained"
								onClick={handleBack}
								style={{ width: '48%', color: 'white' }}
							>
								Back
							</Button>
						)}
					<Button
						id={activeStep === steps.length - 1 ? 'ga-signUpWithEmail' : ''}
						className={activeStep === steps.length - 1 ? 'ga-signUp' : ''}
						variant="contained"
						color="primary"
						style={activeStep === 0 ? { width: '100%', color: 'white' } : { width: '48%', color: 'white' }}
						type="submit"
						disabled={!stripe || !elements || disabled}
						data-cy="signUpButton"
					>
						{activeStep === steps.length - 1
							? TranslationHelper.getValue('customer_account.modal.button.signUp')
							: 'Next'}
					</Button>
				</div>
			</form>
		);
	};

	return (
		<div className="signup-modal text-center">
			<LockOutlined style={{
				fill: 'white',
				backgroundColor: theme.palette.primary.main,
				borderRadius: '50%',
				padding: '10px',
				fontSize: '50px',
				marginBottom: '10px'
			}}
			/>
			<h3>Sign up</h3>
			{paymentEnabled || (customerAttributes && customerAttributes[0])
				? (
					<Stepper
						activeStep={activeStep}
						alternativeLabel
						style={{ padding: '10px 0px 0px 0px', margin: '0px -20px' }}
					>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel classes={{ label: classes.step_label_root }}>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				)
				: null}
			<div>
				<div>
					<Typography>{getStepContent({ activeStep, props, inputFieldsSignUp })}</Typography>
				</div>
			</div>
			<div style={{ marginTop: '5px' }}>
				<a
					href="#/"
					onClick={props.loginLayout === 'default'
						? ((e) => { e.preventDefault(); props.setToggleSignUp(false); })
						: ((e) => { e.preventDefault(); window.location.href = '/login'; })}
					style={{ cursor: 'pointer' }}
				>
					Already have an account? Sign in
				</a>
			</div>
		</div>
	);
}
